(() => {
  const init = (root) => {
    if (!root || root.dataset.fitReady === 'true') return;
    root.dataset.fitReady = 'true';

    const state = {
      step: 1,
      species: ''
    };

    const steps = [...root.querySelectorAll('[data-step]')];
    const result = root.querySelector('[data-result]');
    const progress = root.querySelector('[data-progress]');
    const refsEl = root.querySelector('[data-fit-references]');

    let references = [];

    try {
      references = JSON.parse(refsEl?.textContent || '[]');
    } catch (_) {
      references = [];
    }

    const limits = {
      maxWeight: Number(root.dataset.maxWeight),
      maxLength: Number(root.dataset.maxLength),
      maxHeight: Number(root.dataset.maxHeight)
    };

    const tolerance = {
      weight: 3,
      length: 2,
      height: 4
    };

    const getInput = (name) => {
      return Number(root.querySelector(`[data-${name}]`)?.value);
    };

    const focusHeading = (heading) => {
      if (!heading) return;

      heading.setAttribute('tabindex', '-1');
      heading.focus();
    };

    const showStep = (step, moveFocus = true) => {
      state.step = step;

      steps.forEach((el) => {
        const active = Number(el.dataset.step) === step;

        el.hidden = !active;
        el.classList.toggle('is-active', active);

        const error = el.querySelector('[data-error]');

        if (error) {
          error.hidden = true;
        }
      });

      result.hidden = true;
      progress.style.width = `${step * 25}%`;

      if (moveFocus) {
        focusHeading(
          root.querySelector(`[data-step="${step}"] h3`)
        );
      }
    };

    const validate = () => {
      const current = root.querySelector(
        `[data-step="${state.step}"]`
      );

      const error = current?.querySelector('[data-error]');

      let valid = true;

      if (state.step === 1) {
        valid = Boolean(state.species);
      }

      if (state.step === 2) {
        valid =
          Number.isFinite(getInput('weight')) &&
          getInput('weight') > 0;
      }

      if (state.step === 3) {
        valid =
          Number.isFinite(getInput('length')) &&
          getInput('length') > 0;
      }

      if (state.step === 4) {
        valid =
          Number.isFinite(getInput('height')) &&
          getInput('height') > 0;
      }

      if (error) {
        error.hidden = valid;
      }

      return valid;
    };

    const makeButton = (label, secondary, handler) => {
      const btn = document.createElement('button');

      btn.type = 'button';
      btn.className = secondary
        ? 'button button--secondary'
        : 'button';

      btn.textContent = label;
      btn.addEventListener('click', handler);

      return btn;
    };

    const renderReferences = (weight, outcome) => {
      const wrap = root.querySelector(
        '[data-reference-wrap]'
      );

      const grid = root.querySelector(
        '[data-reference-grid]'
      );

      if (!wrap || !grid) return;

      grid.innerHTML = '';

      if (outcome === 'red') {
        wrap.hidden = true;
        return;
      }

      const nearest = references
        .filter((ref) => ref.species === state.species)
        .sort((a, b) => {
          const distance =
            Math.abs(Number(a.weight) - weight) -
            Math.abs(Number(b.weight) - weight);

          if (distance !== 0) {
            return distance;
          }

          return (
            Number(a.sortPriority || 999) -
            Number(b.sortPriority || 999)
          );
        })
        .slice(0, 3);

      if (nearest.length === 0) {
        wrap.hidden = true;
        return;
      }

      wrap.hidden = false;

      nearest.forEach((ref) => {
        const card = document.createElement('article');

        card.className =
          'rover-fit__reference-card';

        if (ref.image) {
          const media = document.createElement('div');

          media.className =
            'rover-fit__reference-image';

          const img = document.createElement('img');

          img.src = ref.image;

          img.alt =
            `${ref.breed}, ${ref.weight} lb, ` +
            'in the Roverlund Out-of-Office pet carrier';

          img.loading = 'lazy';

          media.appendChild(img);
          card.appendChild(media);
        }

        const copy = document.createElement('div');

        copy.className =
          'rover-fit__reference-copy';

        const title =
          document.createElement('strong');

        title.textContent =
          `${ref.breed} · ${ref.weight} LBS`;

        const meta =
          document.createElement('span');

        meta.textContent =
          ref.fitClassification ||
          'Comfortable fit';

        const note =
          document.createElement('span');

        note.textContent =
          ref.note ||
          ref.fitClassification ||
          'Real pet reference';

        copy.append(
          title,
          meta,
          note
        );

        card.appendChild(copy);
        grid.appendChild(card);
      });
    };

    const renderResult = () => {
      const weight = getInput('weight');
      const length = getInput('length');
      const height = getInput('height');

      const overWeight =
        Math.max(
          0,
          weight - limits.maxWeight
        );

      const overLength =
        Math.max(
          0,
          length - limits.maxLength
        );

      const overHeight =
        Math.max(
          0,
          height - limits.maxHeight
        );

      /*
       * FIT LOGIC
       *
       * GREEN — LIKELY A GOOD FIT
       * All measurements are within the recommended
       * fit guidelines.
       *
       * AMBER — LIKELY A COMFORTABLE FIT
       * One, two, or all three measurements may be above
       * the recommended guidelines, provided they remain
       * within the extended comfortable-fit tolerances:
       *
       * - weight: up to +3 LBS
       * - body length: up to +2 IN
       * - shoulder height: up to +4 IN
       *
       * Length and height may BOTH be over the recommended
       * guideline at the same time and still return amber.
       *
       * RED — DOUBLE-CHECK
       * Any measurement beyond the extended tolerance.
       */

      const withinRecommended =
        overWeight === 0 &&
        overLength === 0 &&
        overHeight === 0;

      const withinComfortableTolerance =
        overWeight <= tolerance.weight &&
        overLength <= tolerance.length &&
        overHeight <= tolerance.height;

      const outcome =
        withinRecommended
          ? 'green'
          : withinComfortableTolerance
            ? 'amber'
            : 'red';

      const messaging = {
        green: {
          title: 'LIKELY A GOOD FIT',
          copy:
            'Based on the measurements you entered, your pet falls within our recommended fit guidelines for the Out-of-Office Carrier.'
        },

        amber: {
          title: 'LIKELY A COMFORTABLE FIT',
          copy:
            'Based on the measurements you entered, your pet is likely to be a comfortable fit. The carrier is soft-sided, and pets do not need to stand fully upright inside. Body shape and how your pet settles or curls up also play an important role in fit.'
        },

        red: {
          title:
            'WE RECOMMEND DOUBLE-CHECKING THEIR FIT',
          copy:
            'One or more measurements fall outside the range where we can confidently recommend the carrier based on measurements alone. Send us their measurements and we’ll help you make the right call.'
        }
      }[outcome];

      steps.forEach((el) => {
        el.hidden = true;
      });

      result.hidden = false;
      progress.style.width = '100%';

      root.querySelector(
        '[data-result-title]'
      ).textContent = messaging.title;

      root.querySelector(
        '[data-result-copy]'
      ).textContent = messaging.copy;

      root.querySelector(
        '[data-summary-weight]'
      ).textContent = `${weight} LBS`;

      root.querySelector(
        '[data-summary-length]'
      ).textContent = `${length} IN`;

      root.querySelector(
        '[data-summary-height]'
      ).textContent = `${height} IN`;

      renderReferences(
        weight,
        outcome
      );

      const actions = root.querySelector(
        '[data-result-actions]'
      );

      actions.innerHTML = '';

      if (
        outcome === 'green' ||
        outcome === 'amber'
      ) {
        actions.append(
          makeButton(
            'SHOP THE CARRIER',
            false,
            () => {
              const carrierPath =
                '/products/out-of-office-pet-carrier-1';

              const product = [
                ...document.querySelectorAll(
                  'product-info[data-update-url="true"]'
                )
              ].find((element) => {
                if (!element.dataset.url) {
                  return false;
                }

                const productPath =
                  new URL(
                    element.dataset.url,
                    window.location.origin
                  ).pathname;

                return productPath.endsWith(
                  carrierPath
                );
              });

              if (product) {
                const purchaseControls =
                  product.querySelector(
                    'variant-selects'
                  ) ||
                  product.querySelector(
                    '.product__info-container'
                  ) ||
                  product;

                const submitButton =
                  product.querySelector(
                    'button[name="add"]'
                  );

                const header =
                  document.querySelector(
                    'sticky-header'
                  );

                const headerHeight =
                  header
                    ? header.getBoundingClientRect()
                        .height
                    : 0;

                const top =
                  purchaseControls
                    .getBoundingClientRect()
                    .top +
                  window.scrollY -
                  headerHeight -
                  24;

                if (
                  submitButton &&
                  !submitButton.disabled
                ) {
                  submitButton.focus({
                    preventScroll: true
                  });
                }

                window.scrollTo({
                  top: Math.max(0, top),
                  behavior:
                    window.matchMedia(
                      '(prefers-reduced-motion: reduce)'
                    ).matches
                      ? 'instant'
                      : 'smooth'
                });

                return;
              }

              const storefrontRoot =
                window.Shopify?.routes?.root ||
                '/';

              window.location.href =
                storefrontRoot.replace(
                  /\/$/,
                  ''
                ) + carrierPath;
            }
          ),

          makeButton(
            'REVIEW MEASUREMENTS',
            true,
            () => showStep(2)
          )
        );
      } else {
        actions.append(
          makeButton(
            'ASK US TO DOUBLE-CHECK',
            false,
            () => {
              const subject =
                'Roverlund Fit Check — Please double-check my pet';

              const body = [
                'Hi Roverlund,',
                '',
                'I used the Fit Check and would love your help confirming whether the Out-of-Office Pet Carrier is a good fit for my pet.',
                '',
                `Pet type: ${state.species || ''}`,
                `Weight: ${weight} lbs`,
                `Body length: ${length} in`,
                `Shoulder height: ${height} in`,
                '',
                'I can also attach a side-profile photo of my pet if helpful.',
                '',
                'Thank you!'
              ].join('\n');

              window.location.href =
                `mailto:woof@roverlund.com?subject=${encodeURIComponent(
                  subject
                )}&body=${encodeURIComponent(
                  body
                )}`;
            }
          ),

          makeButton(
            'REVIEW MEASUREMENTS',
            true,
            () => showStep(2)
          )
        );
      }

      focusHeading(
        root.querySelector(
          '[data-result-title]'
        )
      );
    };

    root.addEventListener(
      'click',
      (event) => {
        const pet =
          event.target.closest(
            '[data-pet-type]'
          );

        if (pet) {
          state.species =
            pet.dataset.petType;

          root
            .querySelectorAll(
              '[data-pet-type]'
            )
            .forEach((btn) => {
              btn.setAttribute(
                'aria-pressed',
                btn === pet
                  ? 'true'
                  : 'false'
              );
            });

          const error =
            root.querySelector(
              '[data-step="1"] [data-error]'
            );

          if (error) {
            error.hidden = true;
          }

          return;
        }

        if (
          event.target.closest(
            '[data-next]'
          )
        ) {
          if (
            validate() &&
            state.step < 4
          ) {
            showStep(
              state.step + 1
            );
          }

          return;
        }

        if (
          event.target.closest(
            '[data-back]'
          )
        ) {
          showStep(
            Math.max(
              1,
              state.step - 1
            )
          );

          return;
        }

        if (
          event.target.closest(
            '[data-submit]'
          )
        ) {
          if (validate()) {
            renderResult();
          }
        }
      }
    );

    showStep(1, false);
  };

  const initAll = () => {
    document
      .querySelectorAll(
        '[data-rover-fit]'
      )
      .forEach(init);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initAll,
      {
        once: true
      }
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(
        event.target.querySelector(
          '[data-rover-fit]'
        )
      );
    }
  );
})();